// frontend/src/components/reorderLevels/ReorderLevelsProvider.jsx
//
// The one home of the reorder-levels popups. Mounted once in AppLayout, it
// renders the welcome popup and the grid dialog, and hands every page
// `useReorderLevels()` (./useReorderLevels.js).

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';

import DialogErrorBanner from '../common/DialogErrorBanner';
import { needsLocaleGate } from '../common/LocaleGate';
import ReorderLevelsDialog from './ReorderLevelsDialog';
import { ReorderLevelsContext } from './useReorderLevels';
import { useAuth } from '../../contexts/AuthContext';
import {
  useDismissReorderLevelsWelcome, useReorderLevelsStatus,
} from '../../hooks/useReorderLevelsData';
import { translateRequestError } from '../../queries/api';
import '../../styles/dialog-form.css';

export function ReorderLevelsProvider({ children }) {
  const status = useReorderLevelsStatus();
  const { user } = useAuth();
  // The grid dialog stays mounted through its fade-out. `opening` keys it, so
  // a dialog reopened while it fades is a new one: every opening starts clean.
  const [dialog, setDialog] = useState({ opening: 0, open: false, mounted: false });
  const open = useCallback(() => setDialog((current) => (
    current.open ? current : { opening: current.opening + 1, open: true, mounted: true }
  )), []);
  const close = useCallback(() => setDialog((current) => ({ ...current, open: false })), []);
  const unmount = useCallback(() => setDialog((current) => ({ ...current, mounted: false })), []);
  const value = useMemo(() => ({ open, status }), [open, status]);

  // The welcome waits for the language popup (LocaleGate), which comes first
  // and cannot be dismissed.
  const showWelcome = status?.show_welcome === true && !!user && !needsLocaleGate(user);

  return (
    <ReorderLevelsContext.Provider value={value}>
      {children}
      <WelcomeDialog open={showWelcome} onOpenLevels={open} />
      {dialog.mounted && (
        <ReorderLevelsDialog
          key={dialog.opening}
          open={dialog.open}
          onClose={close}
          onExited={unmount}
          currentMonth={status === null ? null : status.current_month}
        />
      )}
    </ReorderLevelsContext.Provider>
  );
}

// Shown once per store: both buttons, Escape and a click outside all record
// the dismissal, and the popup closes on the status that write answers with.
function WelcomeDialog({ open, onOpenLevels }) {
  const { t } = useTranslation();
  const dismiss = useDismissReorderLevelsWelcome();
  const [error, setError] = useState(null);

  const dismissThen = async (next) => {
    setError(null);
    try {
      await dismiss.mutateAsync();
    } catch (err) {
      console.error('Reorder levels welcome dismissal failed:', err.message);
      setError(translateRequestError(err, t));
      return;
    }
    next?.();
  };

  return (
    <Dialog
      open={open}
      onClose={dismiss.isPending ? undefined : () => dismissThen()}
      className="dialog-form dialog-form--confirm"
    >
      <DialogTitle>{t('reorderLevels.welcome.title')}</DialogTitle>
      <DialogContent>
        {t('reorderLevels.welcome.body').split('\n\n').map((paragraph) => (
          <Typography key={paragraph} variant="body2">{paragraph}</Typography>
        ))}
        <DialogErrorBanner error={error} />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => dismissThen()} disabled={dismiss.isPending}>
          {t('reorderLevels.welcome.notNow')}
        </Button>
        <Button
          variant="contained"
          onClick={() => dismissThen(onOpenLevels)}
          loading={dismiss.isPending}
        >
          {t('reorderLevels.title')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
